import cn from 'classnames';
import { useState, useEffect } from 'react';

import { useCheckoutContext, SupportedCountry } from '@zooplus/checkout-state';
import { isBitiba } from '@zooplus/commons-lang';
import { Cross } from '@zooplus/icons';
import { H4, P1, Anchor, DropdownMenu, PrimaryButton, Input } from '@zooplus/ui-components';

import { getAppConfig } from '@config/get-app-config';
import { useTranslation } from '@hooks/use-translations';
import { CHECKOUT_STATE_PROJECT_ID } from '@utilities/constants';
import { isArrayNotEmpty } from '@utilities/utils';

import styles from './cart-shipping-fees-popover.module.scss';

type ShippingCountryAndZipFormProps = {
  supportedCountries: SupportedCountry[];
  selectedDeliveryCountry: string;
  popoverVisibility: Function;
  deliveryZipCode: string;
};

declare const require: {
  context: (path: string, deep?: boolean, filter?: RegExp) => __WebpackModuleApi.RequireContext;
};

const ShippingCountryAndZipForm = ({
  supportedCountries,
  selectedDeliveryCountry,
  popoverVisibility,
  deliveryZipCode,
}: ShippingCountryAndZipFormProps) => {
  const flags = require.context(`@public/images`, true);
  const { site } = getAppConfig();
  const { t } = useTranslation();
  const { setShippingCountry, isLoading: loadingIndicator } =
    useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);

  const [isOpen, setIsOpen] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [zipCode, setZipCodeValue] = useState<string>(deliveryZipCode || '');
  const [selectedCountry, setSelectedCountry] = useState<SupportedCountry>();
  const [triggerSelectedCountry, setTriggerSelectedCountry] = useState<SupportedCountry>();
  const [isShippingUpdatePending, setIsShippingUpdatePending] = useState<boolean>(false);

  useEffect(() => {
    isArrayNotEmpty(supportedCountries) &&
      supportedCountries.filter(
        (country) =>
          selectedDeliveryCountry === country.countryCode &&
          setTriggerSelectedCountry(selectedCountry || country),
      );
  }, [selectedCountry, selectedDeliveryCountry, supportedCountries]);

  const handleSelectedCountry = (country: SupportedCountry) => {
    setSelectedCountry(country);
    setIsOpen(false);
  };

  const handleShippingFeeChange = () => {
    setIsButtonLoading(true);
    setIsShippingUpdatePending(true);
    setShippingCountry?.({
      country: triggerSelectedCountry?.countryCode || '',
      zipCode: zipCode,
    });
  };

  useEffect(() => {
    if (isShippingUpdatePending && !loadingIndicator) {
      setIsButtonLoading(false);
      popoverVisibility(false);
      setIsShippingUpdatePending(false);
    }
  }, [loadingIndicator, isShippingUpdatePending, popoverVisibility]);

  const triggerLabel = () => {
    return (
      triggerSelectedCountry && (
        <>
          <img
            loading="lazy"
            src={flags(`./${triggerSelectedCountry?.iso3166Name}.png`).default}
            alt={triggerSelectedCountry?.countryName}
          />
          <P1 className={styles.dropdownSelectedCountry}>
            {selectedCountry?.countryName
              ? selectedCountry.countryName
              : triggerSelectedCountry.countryName}
          </P1>
        </>
      )
    );
  };

  const renderDeliveryCountryList = () => {
    return (
      <ul className={styles.dropdownListWrapper}>
        {isArrayNotEmpty(supportedCountries) &&
          supportedCountries?.map((country, index) => {
            return (
              <button
                key={`{${index}_${country.countryCode}}`}
                className={`${cn(
                  styles.countryListStyling,
                  (selectedCountry?.countryCode || selectedDeliveryCountry) === country.countryCode
                    ? styles.selectedDropdownListItem
                    : styles.nonSelectedDropdownListItem,
                )}`}
                type="button"
                onClick={() => handleSelectedCountry(country)}>
                <li data-value={country.countryCode} data-label={country.countryName}>
                  <img
                    loading="lazy"
                    className={styles.flagImg}
                    src={flags(`./${country.iso3166Name}.png`).default}
                    alt={`flag-${country.iso3166Name}`}
                  />
                  <P1>{country.countryName}</P1>
                </li>
              </button>
            );
          })}
      </ul>
    );
  };

  return (
    <div className={styles.popoverContentWrapper} data-testid="bpa-popover-template">
      <div className={styles.popoverTitleWrapper}>
        <H4>{t('cart.summary.shippingFees')}</H4>
        <Anchor
          tabIndex={0}
          data-testid="shipping-cost-popover-close"
          onClick={() => popoverVisibility(false)}>
          <Cross height={20} width={20} />
        </Anchor>
      </div>
      <DropdownMenu
        className={cn(styles.dropdownWrapper, styles.setMargin)}
        data-testid="shipping-cost-dropdown"
        breakpoint="all"
        menuId="dd-menu-BreakpointAll"
        tabIndex={0}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        isOpen={isOpen}
        triggerLabel={triggerLabel()}>
        {renderDeliveryCountryList()}
      </DropdownMenu>
      <Input
        className={cn(styles.setMargin, styles.countryCodeInput)}
        data-testid="shipping-cost-zipcode"
        tabIndex={0}
        inputProps={{
          placeholder: t('cart.summary.shippingFeesPopover.postcode'),
          value: zipCode,
          maxLength: 10,
        }}
        onChange={(e) => setZipCodeValue(e.target.value)}
      />
      <PrimaryButton
        className={styles.updateShippingCost}
        data-testid="shipping-cost-popover-action"
        length="full"
        tabIndex={0}
        loadingIconType={isBitiba(site.domain) ? 'spinner' : 'paw'}
        isLoading={isButtonLoading}
        onClick={() => handleShippingFeeChange()}>
        {t('cart.summary.shippingFeesPopover.update')}
      </PrimaryButton>
    </div>
  );
};

export default ShippingCountryAndZipForm;
